import i18n from '@/utils/i18n';
import {
  AgentColumnId,
  PricingRecommendationRow,
} from '@/experiences/sales-portal/domain/pricing-agent/screens/types';
import { buildBookmarkedSection } from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-special-sections';
import {
  buildRangeSections,
  buildUOMSection,
} from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-column-sections';
import {
  PillFilterSection,
  PricingFilterOption,
  PricingFilterSection,
  PricingFilterSectionKind,
  PricingFilterSpecialSectionId,
  PricingRangeColumnConfig,
} from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-types';
import { RecommendationAction } from '@/experiences/sales-portal/domain/pricing-agent/blocs/stores/recommendation-actions-store-types';

export const AGENT_UNREVIEWED_STATUS = 'unreviewed';

const STATUS_VALUES = [
  RecommendationAction.Accepted,
  RecommendationAction.Rejected,
  RecommendationAction.Overridden,
];

const STATUS_LABEL_KEYS: Record<string, string> = {
  [AGENT_UNREVIEWED_STATUS]: 'pricing.agent.review.status.unreviewed',
  [RecommendationAction.Accepted]: 'pricing.agent.review.status.accepted',
  [RecommendationAction.Overridden]: 'pricing.agent.review.status.overridden',
  [RecommendationAction.Rejected]: 'pricing.agent.review.status.rejected',
};

const toStatusOption = (value: string): PricingFilterOption => ({
  label: i18n.t(STATUS_LABEL_KEYS[value]),
  value,
});

interface BuildAgentFilterSectionsArgs {
  bookmarkedByRecommendation?: Record<string, boolean>;
  columnIds: string[];
  getEffectiveStatus: (
    row: PricingRecommendationRow,
  ) => RecommendationAction | null;
  includeUnreviewed: boolean;
  rangeColumnConfigs: Record<
    string,
    PricingRangeColumnConfig<PricingRecommendationRow>
  >;
  rows: PricingRecommendationRow[];
}

const buildStatusSection = (
  getEffectiveStatus: (
    row: PricingRecommendationRow,
  ) => RecommendationAction | null,
  includeUnreviewed: boolean,
): PillFilterSection<PricingRecommendationRow> => {
  const options = STATUS_VALUES.map(toStatusOption);

  if (includeUnreviewed) {
    options.push(toStatusOption(AGENT_UNREVIEWED_STATUS));
  }

  return {
    getValues: (row) => [getEffectiveStatus(row) ?? AGENT_UNREVIEWED_STATUS],
    id: PricingFilterSpecialSectionId.Status,
    kind: PricingFilterSectionKind.Pill,
    options,
    singleSelect: false,
    title: i18n.t('pricing.filters.status.title'),
  };
};

export const buildAgentFilterSections = ({
  bookmarkedByRecommendation,
  columnIds,
  getEffectiveStatus,
  includeUnreviewed,
  rangeColumnConfigs,
  rows,
}: BuildAgentFilterSectionsArgs): PricingFilterSection<PricingRecommendationRow>[] => {
  const sections: PricingFilterSection<PricingRecommendationRow>[] = [
    buildStatusSection(getEffectiveStatus, includeUnreviewed),
  ];

  if (bookmarkedByRecommendation) {
    sections.push(
      buildBookmarkedSection(bookmarkedByRecommendation, (row) => row.uuid),
    );
  }

  for (const columnId of columnIds) {
    if (columnId === AgentColumnId.UOM) {
      sections.push(buildUOMSection(columnId, rows));
      continue;
    }

    const config = rangeColumnConfigs[columnId];
    if (!config) {
      continue;
    }

    sections.push(...buildRangeSections(columnId, config, rows));
  }

  return sections;
};
